import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, ChevronRight, ChevronLeft, Building2, Users, Wallet, Home, FileText, PenTool, Upload, Download, Menu, X, UserPlus, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { downloadPdf } from "@/lib/downloadPdf";
import { useConfirmDialog } from "@/lib/useConfirmDialog";  // PR #95 / UX-1
import { useNoIndex } from "@/lib/useNoIndex";  // PR #97 / R8

// Import step components
import { StepClientRegistration } from "@/components/steps/StepClientRegistration";
import { StepLoanType } from "@/components/steps/StepLoanType";
import { StepCompanyBorrower } from "@/components/steps/StepCompanyBorrower";
import { StepGuarantors } from "@/components/steps/StepGuarantors";
import { StepAssetsLiabilities } from "@/components/steps/StepAssetsLiabilities";
import { StepSecurityDetails } from "@/components/steps/StepSecurityDetails";
import { StepLoanDetails } from "@/components/steps/StepLoanDetails";
import { StepDeclarations } from "@/components/steps/StepDeclarations";
import { StepDocumentsReview } from "@/components/steps/StepDocumentsReview";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_HORIZONTAL = "/logos/solara-horizontal.png";

// SWF-18/25 (2026-10-27) — website hand-off attribution. solaraglobal.com.au's
// short form links here as /apply?ref=L-…&loan=…&amount=…&utm_*=…&channel=…&ref_host=…
// (never name/phone/email). Kept in sessionStorage so it survives the Step 1
// sign-up, then posted once as `source` when the draft is created.
const HANDOFF_KEY = "solara_apply_source";
const HANDOFF_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "channel", "ref_host"];
function readHandoffSource(params) {
  const fromUrl = {};
  if (params.get("ref")) fromUrl.web_lead_id = params.get("ref");
  HANDOFF_FIELDS.forEach((k) => { if (params.get(k)) fromUrl[k] = params.get(k); });
  if (params.get("loan")) fromUrl.loan_hint = params.get("loan");
  if (params.get("amount")) fromUrl.amount_hint = params.get("amount");
  if (Object.keys(fromUrl).length) {
    if (fromUrl.web_lead_id || fromUrl.utm_medium === "website") fromUrl.landing = "website";
    try { sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(fromUrl)); } catch { /* storage blocked */ }
    return fromUrl;
  }
  try {
    const saved = sessionStorage.getItem(HANDOFF_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
}

// APPLY-PREFILL-1 (2026-10-30) — the short form already took name, mobile and
// email. The site's Continue link carries a one-time token (&t=…); the form
// swaps it for those details via GET /api/apply/handoff and fills Step 1, so
// the client only has to choose a password. The token is stripped from the
// address bar straight away; the details are kept for this tab only.
const PREFILL_KEY = "solara_apply_prefill";
const SITE_LOAN_TO_CRM = {
  "construction-finance": "construction_finance",
  "mezzanine-preferred-equity": "mezzanine_loan",
  "bridging-loans": "bridging_loan",
  "land-bank-loans": "bridging_loan",
  "commercial-property-loans": "commercial_loan",
  "smsf-commercial-loans": "commercial_loan",
  "low-doc-commercial-loans": "commercial_loan",
  "debt-restructure-workout-loans": "commercial_loan",
  "short-term-business-loans": "business_loan",
  "unsecured-business-loans": "business_loan",
  "business-lines-of-credit": "business_loan",
  "invoice-finance": "business_loan",
  "trade-finance": "business_loan",
  "asset-equipment-finance": "business_loan",
  "ato-debt-refinance": "business_loan",
  "impaired-credit-commercial-loans": "business_loan",
  "rural-agribusiness-loans": "agricultural_loan",
};
function takeHandoffToken(params) {
  const t = params.get("t");
  const ref = params.get("ref");
  if (!t) return null;
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete("t");
    window.history.replaceState(window.history.state, "", u.pathname + u.search + u.hash);
  } catch { /* old browser — harmless, the token is single-use */ }
  return ref ? { ref, t } : null;
}
function readSavedPrefill() {
  try {
    const s = sessionStorage.getItem(PREFILL_KEY);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}
// Fill only empty fields: never overwrite what the client has typed.
function seedFormFromLead(fd, lead, source) {
  const hint = (source && source.loan_hint) || (lead && lead.loan_hint) || "";
  const loanType = SITE_LOAN_TO_CRM[hint] || "";
  const amount = parseInt((source && source.amount_hint) || 0, 10) || 0;
  const cb = fd.company_borrower || {};
  const ld = fd.loan_details || {};
  return {
    ...fd,
    company_borrower: {
      ...cb,
      company_name: cb.company_name || (lead && lead.company_name) || "",
      company_email: cb.company_email || (lead && lead.email) || "",
      contact_number: cb.contact_number || (lead && lead.phone) || "",
    },
    loan_details: {
      ...ld,
      loan_type: ld.loan_type || loanType,
      net_loan_required: ld.net_loan_required || amount,
    },
  };
}

// HF-Session-Leak-2 (2026-09-16) — for the "welcome back" identity gate.
// Shows enough that the real owner recognises their own account at a
// glance, without putting a full email address on screen for anyone who
// happens to be standing at a shared device to read off.
function maskIdentityForConfirm(u) {
  if (!u) return "";
  const email = u.email || "";
  const atIndex = email.indexOf("@");
  let maskedEmail = email;
  if (atIndex > 0) {
    const local = email.slice(0, atIndex);
    const domain = email.slice(atIndex + 1);
    const visible = local.slice(0, 1);
    maskedEmail = `${visible}${"*".repeat(Math.max(local.length - 1, 1))}@${domain}`;
  }
  return u.name ? `${u.name} — ${maskedEmail}` : maskedEmail;
}

// PR #105 / APP-8 — copy review. Descriptions are tightened so the sidebar
// reads as a clear action list (what the client will *do* on each step) rather
// than vague nouns. Mention photos on step 6 so it's discoverable in nav.
const STEPS = [
  { id: 1, title: "Create Account",         icon: UserPlus,  description: "Register or sign in" },
  { id: 2, title: "Loan Type",              icon: Briefcase, description: "Pick the loan that fits" },
  { id: 3, title: "Borrower Details",       icon: Building2, description: "Company, contact & financials" },
  { id: 4, title: "Guarantors",             icon: Users,     description: "Personal details for guarantors" },
  { id: 5, title: "Assets & Liabilities",   icon: Wallet,    description: "Snapshot of your position" },
  { id: 6, title: "Security",               icon: Home,      description: "Properties offered + photos" },
  { id: 7, title: "Loan Details",           icon: FileText,  description: "Amount, term, purpose, fees" },
  { id: 8, title: "Declarations",           icon: PenTool,   description: "Read, agree, sign" },
  { id: 9, title: "Documents & Review",     icon: Upload,    description: "Upload supporting docs & submit" },
];

const initialRegistrationData = {
  full_name: "",
  email: "",
  password: "",
  confirm_password: "",
  phone: "",
  company_name: "",
  signin_mode: false,
};

const initialFormData = {
  company_borrower: {
    company_name: "",
    acn_abn: "",
    company_email: "",
    industry_type: "",
    positions: [],
    contact_number: "",
    annual_income: 0,
    is_trustee: false,
    trustee_name: "",
    is_smsf_trustee: false,
    registered_address: {
      unit_no: "",
      street_no: "",
      street_name: "",
      suburb: "",
      state: "",
      postcode: "",
    },
  },
  solvency_enquiries: {
    pending_litigation: false,
    unsatisfied_judgements: false,
    bankrupt_past_5_years: false,
    credit_refused_1_year: false,
    outstanding_ato_debts: false,
    outstanding_tax_returns: false,
    arrangement_with_creditor: false,
  },
  guarantors: [],
  company_assets_liabilities: [],
  guarantor_assets_liabilities: [],
  properties: [],
  // APP-FORM-FIX (2026-07-25) — explicit "no security property" flag; see
  // backend/models.py LoanApplication.no_security_property for why.
  no_security_property: false,
  loan_details: {
    loan_type: "",
    net_loan_required: 0,
    term_months: 0,
    proposed_settlement_date: "",
    expected_rate: "",
    loan_purposes: [],
    loan_purpose_other: "",
    additional_comments: "",
    submitted_to_other_providers: false,
    other_providers_details: "",
  },
  loan_requirements: [],
  exit_strategy: {
    methods: [],
    other_details: "",
  },
  declarations: {
    confirmed_true_correct: false,
    privacy_acknowledged: false,
  },
  declaration_signatures: {
    borrower_signatures: [],
    guarantor_signatures: [],
  },
  uploaded_documents: [],
  current_step: 1,
};

export default function LoanApplicationForm() {
  useNoIndex();
  const navigate = useNavigate();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();  // PR #95 / UX-1
  const { user, register, login, logout, hasFreshAuth, confirmIdentity } = useAuth();
  // PR HF-Apply-2 (2026-06-03) — query-param entry points:
  //   ?resume=<application_id>  → load THAT specific draft (from /my-applications "Continue" button)
  //   ?new=1                    → force a fresh application (from "+ New Application" button)
  // Without either, behaviour matches the historical "resume most recent draft" default.
  const [searchParams] = useSearchParams();
  // SWF-18/25 — capture the hand-off on first render (before any sign-up redirect drops the query string).
  const handoffRef = useRef(null);
  if (handoffRef.current === null) handoffRef.current = readHandoffSource(searchParams) || false;
  const resumeAppIdParam = searchParams.get("resume") || null;
  // CLIENT-LINK-1 — the section a completion link asked the client to start on.
  const resumeStepParam = Number.parseInt(searchParams.get("step") || "", 10) || null;
  const forceNewParam = searchParams.get("new") === "1";
  // PR HF-Apply-2 — guard against double-fire of handleNext. React's
  // disabled={isLoading} can lag a real user double-click by one render frame,
  // which previously caused the registration flow to run twice → two
  // applications created → exactly the 4-draft mess Mark saw. useRef updates
  // synchronously so the second click bails before doing anything.
  const inFlightRef = useRef(false);
  // If user is already authed when the form mounts, skip Step 1 (account creation) and start at Step 2 (Company Details).
  const [currentStep, setCurrentStep] = useState(user ? 2 : 1);
  // PR HF-Apply-13 (2026-06-26) — track the highest step the user has
  // reached separately from currentStep. Audit issue HIGH-4: if a user
  // advanced to Step 5 then went Back to Step 3 and closed the tab, the
  // next saveProgress wrote current_step=3 and they re-hydrated at Step 3
  // on resume, losing their forward progress. Now resume uses the higher
  // of current_step and max_step_reached.
  const [maxStepReached, setMaxStepReached] = useState(user ? 2 : 1);
  const [formData, setFormData] = useState(() => {
    // HF-Draft-Leak-1 (2026-07-31) — this used to read a FIXED, non-user-scoped
    // key ("solara_draft"), so the next person to open /apply on the same
    // browser was handed the previous applicant's company details, guarantor
    // names, DOBs, addresses, assets/liabilities and security valuations,
    // pre-filled into their form. It was written every second and only removed
    // on a SUCCESSFUL submit — an abandoned application persisted indefinitely,
    // and logout() never touched it.
    //
    // The draft is now keyed per user (see draftKey below). Note an anonymous
    // visitor can only be on step 1 (account creation) — the form starts at
    // step 2 only when `user` is set — so no borrower PII is ever entered
    // before we have a user id to scope by.
    //
    // Also purge the legacy unscoped key on mount: it may still be holding a
    // previous applicant's data in staff and shared browsers right now.
    try { localStorage.removeItem("solara_draft"); } catch { /* blocked */ }
    if (!user?.id) return initialFormData;
    const saved = localStorage.getItem(`solara_draft:${user.id}`);
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { /* ignore */ }
    }
    return initialFormData;
  });
  // Per-user localStorage key for the offline/session-expiry draft rescue.
  // Null when unauthenticated — callers must skip persistence in that case.
  const draftKey = user?.id ? `solara_draft:${user.id}` : null;
  const [registrationData, setRegistrationData] = useState(initialRegistrationData);
  const [applicationId, setApplicationId] = useState(null);
  const [applicationRef, setApplicationRef] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // If the user is already authed, mark Step 1 (registration) complete from the start.
  const [completedSteps, setCompletedSteps] = useState(user ? [1] : []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [prefillData, setPrefillData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // APPLY-3 (2026-07-08) — flips true if the draft still hasn't loaded after
  // 20s so the sidebar skeleton can offer a reload instead of pulsing forever.
  const [draftLoadTimedOut, setDraftLoadTimedOut] = useState(false);
  // APP-FORM-FIX (2026-07-25) — background auto-save status for the subtle
  // "Saving… / Saved" indicator. See the debounced-save effect below.
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // idle | saving | saved
  const autoSaveTimerRef = useRef(null);
  const savedIndicatorTimerRef = useRef(null);
  // APPLY-PREFILL-1 — details from the website short form (see PREFILL_KEY).
  const handoffTokenRef = useRef(undefined);
  if (handoffTokenRef.current === undefined) handoffTokenRef.current = takeHandoffToken(searchParams);
  const [leadPrefill, setLeadPrefill] = useState(readSavedPrefill);
  useEffect(() => {
    const h = handoffTokenRef.current;
    if (!h || (leadPrefill && leadPrefill.ref === h.ref)) return undefined;
    let cancelled = false;
    axios.get(`${API}/apply/handoff`, { params: { ref: h.ref, t: h.t } })
      .then((res) => {
        if (cancelled || !res.data) return;
        try { sessionStorage.setItem(PREFILL_KEY, JSON.stringify(res.data)); } catch { /* storage blocked */ }
        setLeadPrefill(res.data);
      })
      .catch(() => { /* expired/spent token — the form simply starts empty */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!leadPrefill || user) return;
    setRegistrationData((prev) => ({
      ...prev,
      full_name: prev.full_name || leadPrefill.full_name || "",
      email: prev.email || leadPrefill.email || "",
      phone: prev.phone || leadPrefill.phone || "",
      company_name: prev.company_name || leadPrefill.company_name || "",
    }));
  }, [leadPrefill, user]);
  // APPLY-PREFILL-2 (2026-10-30) — the website now sends the client straight
  // here after the short form, so /apply opens with an "enquiry received —
  // finish the remaining steps" banner instead of a thank-you box on the site.
  const enquiryRef =
    (leadPrefill && leadPrefill.ref) ||
    (handoffRef.current && handoffRef.current.web_lead_id) ||
    (searchParams.get("ref") && /^L-\d{4}-\d{6}$/.test(searchParams.get("ref")) ? searchParams.get("ref") : "");
  const [enquiryBannerHidden, setEnquiryBannerHidden] = useState(() => {
    try { return sessionStorage.getItem("solara_apply_banner_closed") === "1"; } catch { return false; }
  });
  const closeEnquiryBanner = () => {
    try { sessionStorage.setItem("solara_apply_banner_closed", "1"); } catch { /* storage blocked */ }
    setEnquiryBannerHidden(true);
  };
  // Register → Continue and the auth effect below can both ask for the draft
  // in the same tick; share one request so a new client gets one draft, not two.
  const loadInFlightRef = useRef(null);

  const validateStep = (step) => {
    const errors = [];
    const cb = formData.company_borrower;
    const addr = cb.registered_address || {};
    const loan = formData.loan_details;
    const guarantors = formData.guarantors || [];
    const dec = formData.declarations || {};

    switch (step) {
      case 1: {
        // Step 1 = client registration (or sign-in). If already authed, no fields to validate.
        if (user) break;
        const r = registrationData;
        if (r.signin_mode) {
          if (!r.email?.trim()) errors.push("Email is required");
          if (!r.password) errors.push("Password is required");
        } else {
          if (!r.full_name?.trim()) errors.push("Full Name is required");
          if (!r.email?.trim()) errors.push("Email is required");
          if (!r.phone?.trim()) errors.push("Phone is required");
          if (!r.password) errors.push("Password is required");
          // HF-Apply-8 (2026-06-24) — match the on-screen helper text
          // ("At least 8 characters, with a letter and a digit"). Previously
          // validation accepted 6+ chars even though the UI promised 8 — the
          // mismatch could ship a weak password while looking secure.
          if (r.password && r.password.length < 8) errors.push("Password must be at least 8 characters");
          if (r.password && r.password.length >= 8 && !(/[A-Za-z]/.test(r.password) && /\d/.test(r.password))) {
            errors.push("Password must contain at least one letter and one digit");
          }
          if (r.password !== r.confirm_password) errors.push("Passwords do not match");
        }
        break;
      }
      case 2:
        // PR #26: loan-type selector. One required choice.
        if (!loan?.loan_type) errors.push("Please choose a loan type");
        break;
      case 3:
        if (!cb.company_name?.trim()) errors.push("Company Name is required");
        if (!cb.acn_abn?.trim()) errors.push("ACN/ABN is required");
        if (!addr.street_name?.trim()) errors.push("Street Name is required");
        if (!addr.suburb?.trim()) errors.push("Suburb is required");
        if (!addr.state?.trim()) errors.push("State is required");
        if (!addr.postcode?.trim()) errors.push("Postcode is required");
        break;
      case 4:
        if (guarantors.length === 0) errors.push("At least one guarantor is required");
        guarantors.forEach((g, i) => {
          const hasLegacyFullName = Boolean(g.full_name?.trim());
          const hasStructuredName = Boolean(g.given_names?.trim() && g.surname?.trim());
          if (!hasLegacyFullName && !hasStructuredName) errors.push(`Guarantor ${i + 1}: Full Name is required`);
        });
        break;
      case 5:
        // Assets & Liabilities — no strict required fields, allow pass
        break;
      case 6:
        // Security details — no strict required fields
        break;
      case 7:
        if (!loan.net_loan_required || loan.net_loan_required <= 0) errors.push("Net Loan Amount is required");
        if (!loan.term_months || loan.term_months <= 0) errors.push("Loan Term (Months) is required");
        if (!loan.loan_purposes?.length) errors.push("At least one Loan Purpose must be selected");
        break;
      case 8:
        if (!dec.confirmed_true_correct || !dec.privacy_acknowledged) errors.push("You must confirm the declaration is true and correct");
        if (!formData.declaration_signatures?.borrower_signatures?.some((sig) => sig.signature_data)) errors.push("Borrower signature is required");
        if ((formData.guarantors || []).length > 0 && !formData.declaration_signatures?.guarantor_signatures?.some((sig) => sig.signature_data)) errors.push("Guarantor signature is required");
        break;
      case 9:
        break;
      default:
        break;
    }
    return errors;
  };

  // Create OR resume the draft application AS SOON AS we have an authenticated user.
  // - For unauthed users: hold off until they complete Step 1 (registration / sign-in).
  // - For authed users with an existing in-progress draft: resume the most recent one
  //   so they keep the same application reference across visits.
  // - For authed users with no draft: create a fresh one.
  //
  // HF-Session-Leak-2 (2026-09-16) — also gated on hasFreshAuth. `user` gets
  // populated the instant AuthContext finds ANY valid token in localStorage,
  // including one left behind by a previous, unrelated person on a shared
  // device. loadOrCreateApplication() fetches and hydrates that identity's
  // most recent in-progress draft — full borrower/guarantor/asset/security
  // PII — straight into the form. Do not do that until the person has
  // either (a) proven who they are in THIS tab this session (password/
  // magic-link/passkey login or fresh registration — see AuthContext), or
  // (b) explicitly confirmed the "welcome back" gate below. See the
  // `if (user && !hasFreshAuth)` render branch near the end of this
  // component for the confirmation UI itself.
  useEffect(() => {
    if (user && hasFreshAuth && !applicationId) {
      loadOrCreateApplication();
      fetchPrefillData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasFreshAuth, applicationId]);

  // Auto-save to localStorage on form changes (debounced).
  // HF-Draft-Leak-1: per-user key, and a no-op while unauthenticated.
  useEffect(() => {
    if (!draftKey) return undefined;
    const timer = setTimeout(() => {
      try { localStorage.setItem(draftKey, JSON.stringify(formData)); } catch { /* blocked/full */ }
    }, 1000);
    return () => clearTimeout(timer);
  }, [formData, draftKey]);

  // APP-FORM-FIX (2026-07-25) — debounced auto-save to the BACKEND, not just
  // localStorage. Previously the server only ever saw a step's data when the
  // user clicked Continue / Save Progress / Previous. Multi-row sections like
  // Assets & Liabilities let clients add several rows without triggering any
  // of those — if they then closed the tab, lost the session, or the browser
  // storage was cleared, everything they'd typed was gone server-side (it
  // only ever lived in this browser's localStorage). This mirrors the same
  // debounce-on-change pattern the step components already use to push local
  // row state up into `formData`, just one level further: formData → server.
  //
  // Gated on hasUnsavedChanges so we don't fire on initial mount / draft
  // hydration (hydrateDraftIntoForm's setFormData doesn't set that flag) —
  // only on actual user edits (updateFormData sets it). saveProgress()
  // itself resets hasUnsavedChanges to false on success, which lets this
  // effect naturally go quiet again until the next edit.
  useEffect(() => {
    if (!applicationId || !hasUnsavedChanges) return undefined;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus("saving");
      const saved = await saveProgress();
      if (saved) {
        setAutoSaveStatus("saved");
        if (savedIndicatorTimerRef.current) clearTimeout(savedIndicatorTimerRef.current);
        savedIndicatorTimerRef.current = setTimeout(() => setAutoSaveStatus("idle"), 2500);
      } else {
        setAutoSaveStatus("idle");
      }
    }, 1500);

    return () => clearTimeout(autoSaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, applicationId, hasUnsavedChanges]);

  // PR HF-Apply-13 (2026-06-26) — session-expiry rescue. Audit issue HIGH-2:
  // when the JWT expires mid-flow, saveProgress() silently fails and the
  // borrower keeps typing into a form that no longer talks to the server.
  // Install a response interceptor while the apply form is mounted; on 401
  // we flush the current draft to LS and bounce to /login with a return
  // URL so they're brought right back here. localStorage already holds the
  // form data thanks to the debounced effect above, so resume is automatic.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          try {
            // HF-Draft-Leak-1: per-user keys. If the token expired before we
            // ever had a user id there is nothing sensitive to rescue.
            if (draftKey) {
              localStorage.setItem(draftKey, JSON.stringify(formData));
              // Stash the step too so /login → /apply lands them right back.
              localStorage.setItem(`solara_draft_step:${user.id}`, String(currentStep));
            }
          } catch { /* localStorage may be blocked */ }
          // Only show one toast even on a burst of 401s.
          if (!window.__solara_apply_401_handled) {
            window.__solara_apply_401_handled = true;
            toast.info("Your session timed out — sign in again to keep going. Your progress is saved.");
            setTimeout(() => { window.__solara_apply_401_handled = false; }, 5000);
            navigate(`/login?return=${encodeURIComponent("/apply")}`);
          }
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptor);
    // draftKey/user.id included so the rescue never writes to a stale key if
    // the identity behind the session changes while the form is mounted.
  }, [formData, currentStep, navigate, draftKey, user?.id]);

  const fetchPrefillData = async () => {
    try {
      const res = await axios.get(`${API}/prefill-data`);
      setPrefillData(res.data);
    } catch (e) { /* no prefill available */ }
  };

  const createApplication = async () => {
    try {
      const source = handoffRef.current || readHandoffSource(searchParams);
      const seed = seedFormFromLead(initialFormData, leadPrefill, source);
      const response = await axios.post(`${API}/applications`, source ? { ...seed, source } : seed);
      if (response.data.success) {
        if (source) { try { sessionStorage.removeItem(HANDOFF_KEY); } catch { /* storage blocked */ } }
        setFormData((prev) => seedFormFromLead(prev, leadPrefill, source));
        setApplicationId(response.data.id);
        setApplicationRef(response.data.application_reference);
      }
    } catch (error) {
      console.error("Error creating application:", error);
      toast.error("Failed to initialize application");
    }
  };

  // Pick the most-recent draft application for the current user, restore its data
  // into form state, and resume from where they left off. Falls back to creating a
  // fresh application if no draft exists.
  //
  // PR HF-Apply-2 — query-param overrides:
  //   ?resume=<id>  → load THAT specific draft (deep-link from /my-applications).
  //   ?new=1        → skip the load-existing-drafts step, always create a fresh app.
  const loadOrCreateApplication = () => {
    if (!loadInFlightRef.current) {
      loadInFlightRef.current = _loadOrCreateApplication().finally(() => { loadInFlightRef.current = null; });
    }
    return loadInFlightRef.current;
  };
  const _loadOrCreateApplication = async () => {
    try {
      // ?new=1 → user explicitly wants a fresh application (came from
      // "+ New Application"). Don't resume any existing draft.
      if (forceNewParam) {
        await createApplication();
        return;
      }
      // ?resume=<id> → load that specific draft by id. Falls through to the
      // most-recent-draft path if the id is invalid or doesn't belong to the user.
      if (resumeAppIdParam) {
        try {
          const direct = await axios.get(`${API}/applications/${resumeAppIdParam}`);
          if (direct.data) {
            await hydrateDraftIntoForm(direct.data, resumeStepParam);
            return;
          }
        } catch (e) {
          // Fall through — invalid id, expired session, or 403. Use the
          // historical most-recent-draft behaviour instead.
          // APPLY-3 (2026-07-08) — tell the user what happened instead of a
          // silent fallback (MED: "Continue shows a vague error").
          toast.message("We couldn't open that specific draft — resuming your most recent application instead.");
          console.warn(`Resume id ${resumeAppIdParam} couldn't be loaded; falling back to most-recent draft.`);
        }
      }
      // HF-Apply-9 (2026-06-24) — switched from ?status=draft (which
      // silently broke at Spine-1a when status moved from "draft" to
      // substage ids like "lead_new") to ?pre_submit=1, which the
      // backend resolves to submitted_at==None. The old filter never
      // matched, so every /apply visit created a NEW draft instead of
      // resuming — turning a single borrower into 3-5 orphan drafts
      // across a session. Confirmed live in QA today before fix.
      const res = await axios.get(`${API}/applications`, { params: { pre_submit: 1, limit: 1 } });
      const drafts = res.data?.applications || [];
      if (drafts.length > 0) {
        await hydrateDraftIntoForm(drafts[0]);
        return;
      }
      // No drafts found — create a fresh one.
      await createApplication();
    } catch (error) {
      console.error("Error loading drafts:", error);
      // Fall back to creating a new application so the form is still usable.
      await createApplication();
    }
  };

  // PR HF-Apply-2 — extracted from loadOrCreateApplication so both
  // ?resume=<id> and most-recent-draft paths can rehydrate identically.
  const hydrateDraftIntoForm = async (draft, requestedStep = null) => {
    if (!draft) return;
    setApplicationId(draft.id);
    setApplicationRef(draft.application_reference);

    // Restore the saved form fields. We pick only the keys we manage in
    // the form so we don't accidentally overwrite UI-only flags.
    const restorable = [
      "company_borrower",
      "solvency_enquiries",
      "guarantors",
      "company_assets_liabilities",
      "guarantor_assets_liabilities",
      "properties",
      "no_security_property",
      "loan_details",
      "loan_requirements",
      "exit_strategy",
      "declarations",
      "declaration_signatures",
      "submission_copy_email",
    ];
    const restored = {};
    restorable.forEach((key) => {
      if (draft[key] !== undefined && draft[key] !== null) restored[key] = draft[key];
    });
    if (Object.keys(restored).length > 0) {
      setFormData((prev) => ({ ...prev, ...restored }));
    }

    // Resume on the saved step (clamped to >= 2 so we never bounce an
    // authenticated user back to the registration step).
    // PR HF-Apply-13 — use the higher of current_step and max_step_reached
    // so going Back-then-quit doesn't downgrade your resume point.
    const savedCurrent = draft.current_step || 2;
    const savedMax = draft.max_step_reached || savedCurrent;
    const resumeStep = requestedStep
      ? Math.max(2, Math.min(STEPS.length, requestedStep))
      : Math.max(2, Math.min(STEPS.length, savedCurrent, savedMax));
    setCurrentStep(resumeStep);
    setMaxStepReached(Math.max(resumeStep, savedMax));
    // Mark all earlier steps as completed so the sidebar/nav lets them jump back.
    const priorSteps = [];
    for (let i = 1; i < resumeStep; i += 1) priorSteps.push(i);
    setCompletedSteps(priorSteps);

    toast.info(draft.submitted_at
      ? `Opened your application ${draft.application_reference}. Everything you entered before is still here.`
      : `Resumed your draft application ${draft.application_reference}`);
  };

  // WP-6 (2026-08-01) — stable identity, deliberately.
  // Every step component runs `useEffect(..., [localState])` and calls this to
  // push its state up. Those effects were missing `updateFormData` from their
  // dependency arrays; adding it while this was redefined on every render
  // would have made each effect re-fire on every parent render — an update
  // loop, not a lint fix. Both setters are stable, so `[]` is correct and this
  // identity never changes. Do not add dependencies here without checking the
  // step effects in components/steps/.
  const updateFormData = useCallback((section, data) => {
    setHasUnsavedChanges(true);
    setFormData((prev) => ({
      ...prev,
      [section]: data,
    }));
  }, []);

  const saveProgress = async () => {
    // HF-Apply-7 (2026-06-24) — surface a real error if the
    // applicationId is missing instead of silently returning false.
    // Previously this no-op'd the Continue button with zero feedback.
    if (!applicationId) {
      console.error("saveProgress called without an applicationId — Continue will not advance.");
      toast.error("Your draft isn't loaded yet — please refresh the page and try again.");
      return false;
    }

    // APP-FORM-FIX (2026-07-25) — an explicit save (Continue / Save Progress /
    // Previous, or the autosave timer itself firing) makes any still-pending
    // autosave timer redundant. Cancel it so we never fire two overlapping
    // PUTs for the same edit.
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    try {
      const { uploaded_documents, ...saveableFormData } = formData;
      // PR HF-Apply-13 — persist max_step_reached so resume always lands
      // on the highest step the user has touched (see maxStepReached
      // state comment).
      const nextMax = Math.max(maxStepReached, currentStep);
      if (nextMax !== maxStepReached) setMaxStepReached(nextMax);
      await axios.put(`${API}/applications/${applicationId}`, {
        ...saveableFormData,
        current_step: currentStep,
        max_step_reached: nextMax,
      });
      setHasUnsavedChanges(false);
      return true;
    } catch (error) {
      // HF-Apply-7 — surface the REAL backend error (matches the pattern
      // _handleSubmitInner uses after PR HF-Apply-4). The old generic
      // "Failed to save progress" toast hid useful diagnostics like
      // 403 "You can view this deal but cannot edit its fields" — which
      // is exactly the bug that prevented borrowers completing their
      // own application (Team-1 regression, fixed in this PR's backend
      // change to user_can_edit_fields).
      console.error("Error saving progress:", error);
      const detail = error?.response?.data?.detail
        || error?.message
        || "Failed to save progress. Please try again.";
      toast.error(`Save failed: ${detail}`);
      return false;
    }
  };

  const handleNext = async () => {
    // PR HF-Apply-2 — synchronous re-entry guard. setIsLoading(true) below
    // schedules a re-render that disables the button, but React doesn't
    // commit before the await yields, so a fast double-click can fire this
    // function twice and run register() twice → two applications created.
    // useRef updates synchronously; the second click bails immediately.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      await _handleNextInner();
    } finally {
      inFlightRef.current = false;
    }
  };

  const _handleNextInner = async () => {
    const errors = validateStep(currentStep);
    if (errors.length > 0) {
      setValidationErrors(errors);
      toast.error(`Please complete required fields before continuing`);
      // APPLY-3 (2026-07-08) — scroll the error box itself into view (with
      // scroll-margin for the sticky mobile header) instead of raw top-0,
      // which parked the errors underneath the fixed header (LOW).
      setTimeout(() => {
        const box = document.querySelector('[data-testid="validation-errors"]');
        if (box) box.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo({ top: 0, behavior: "smooth" });
      }, 60);
      return;
    }
    setValidationErrors([]);
    setIsLoading(true);

    // Step 1 = client registration (or sign-in). If the user is not yet authed, do that now.
    // We do this BEFORE creating the application so every application has a user_id.
    if (currentStep === 1 && !user) {
      try {
        if (registrationData.signin_mode) {
          const signedIn = await login(registrationData.email.trim(), registrationData.password);
          // STAFF-MFA-1 — staff accounts finish signing in on the login page.
          if (signedIn?.mfaRequired) {
            toast.info("Staff accounts sign in from the login page.");
            setIsLoading(false);
            navigate("/login");
            return;
          }
        } else {
          await register({
            name: registrationData.full_name.trim(),
            email: registrationData.email.trim(),
            password: registrationData.password,
            company_name: registrationData.company_name?.trim() || "",
            phone: registrationData.phone?.trim() || "",
          });
          // Pre-fill the company name onto Step 2 so they don't have to retype it.
          if (registrationData.company_name?.trim() && !formData.company_borrower?.company_name?.trim()) {
            setFormData((prev) => ({
              ...prev,
              company_borrower: {
                ...prev.company_borrower,
                company_name: registrationData.company_name.trim(),
              },
            }));
          }
        }
        // Eagerly load-or-create the draft application now that auth is set on axios.
        // For sign-in: if the user already has an in-progress draft we'll resume it.
        // For fresh registration: there can't be any drafts yet, so this creates a new one.
        // Either way it avoids the race where the user lands on Step 2 before the useEffect fires.
        if (!applicationId) {
          await loadOrCreateApplication();
        }
        toast.success(registrationData.signin_mode ? "Signed in" : "Account created");
      } catch (error) {
        const detail = error?.response?.data?.detail;
        if (detail === "Email already registered") {
          toast.error("This email is already registered. Switch to Sign in.");
          setRegistrationData((prev) => ({ ...prev, signin_mode: true, password: "", confirm_password: "" }));
        } else if (detail === "Invalid email or password") {
          toast.error("Invalid email or password");
        } else {
          toast.error(detail || (registrationData.signin_mode ? "Sign in failed" : "Registration failed"));
        }
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      if (currentStep < STEPS.length) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      }
      return;
    }

    // All other steps: save the draft and advance.
    const saved = await saveProgress();
    setIsLoading(false);

    if (saved) {
      toast.success("Progress saved");
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      if (currentStep < STEPS.length) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      // APPLY-3 (2026-07-08) — persist anything ticked/typed on this screen
      // before navigating back. Continue saves; Back previously didn't, so a
      // user who ticked declarations, went Back, then closed the tab lost
      // those ticks (MED: "Back doesn't persist checklist ticks").
      if (applicationId) saveProgress();
      setValidationErrors([]);
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleStepClick = (stepId) => {
    if (stepId <= currentStep || completedSteps.includes(stepId - 1)) {
      setCurrentStep(stepId);
      window.scrollTo(0, 0);
    }
  };

  const guardedStepClick = async (stepId) => {
    if (!(stepId <= currentStep || completedSteps.includes(stepId - 1))) return;
    if (stepId === currentStep) return;
    await handleProtectedNavigation(() => {
      setValidationErrors([]);
      setCurrentStep(stepId);
      window.scrollTo(0, 0);
    });
  };

  const guardedPrevious = async () => {
    if (currentStep <= 1) return;
    await handleProtectedNavigation(() => {
      setValidationErrors([]);
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    });
  };

  const handleProtectedNavigation = async (navigateAction) => {
    if (!hasUnsavedChanges) {
      navigateAction();
      return;
    }

    // PR #95 / UX-1 — replaced window.confirm with the shadcn dialog.
    const saveBeforeLeaving = await confirm({
      title: "You have unsaved changes",
      description: "Save your progress before leaving this page?",
      confirmLabel: "Save and continue",
      cancelLabel: "Don't save",
    });
    if (saveBeforeLeaving) {
      const saved = await saveProgress();
      if (!saved) return;
      navigateAction();
      return;
    }

    const discardChanges = await confirm({
      title: "Discard unsaved changes?",
      description: "Your edits won't be saved.",
      confirmLabel: "Discard",
      cancelLabel: "Keep editing",
      destructive: true,
    });
    if (!discardChanges) return;
    setHasUnsavedChanges(false);
    navigateAction();
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChanges) return undefined;
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // APPLY-3 (2026-07-08) — draft-load watchdog for the sidebar skeleton.
  // HF-Session-Leak-2 — also gated on hasFreshAuth: nothing is loading yet
  // while the identity-confirmation gate is up, so don't start the clock.
  useEffect(() => {
    if (!user || !hasFreshAuth || applicationId) { setDraftLoadTimedOut(false); return undefined; }
    const t = setTimeout(() => setDraftLoadTimedOut(true), 20000);
    return () => clearTimeout(t);
  }, [user, hasFreshAuth, applicationId]);

  // Auto-scroll the active mobile step pill into view when currentStep changes
  useEffect(() => {
    const el = document.querySelector(`[data-testid="mobile-step-${currentStep}"]`);
    if (el) {
      el.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
    }
  }, [currentStep]);

  const handleSubmit = async () => {
    // PR HF-Apply-2 — same re-entry guard as handleNext. A user spamming the
    // final Submit while the POST is in flight could otherwise submit twice.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      return await _handleSubmitInner();
    } finally {
      inFlightRef.current = false;
    }
  };

  const _handleSubmitInner = async () => {
    if (!applicationId) return;
    
    setIsLoading(true);
    try {
      // Save progress first
      await axios.put(`${API}/applications/${applicationId}`, {
        ...formData,
        current_step: currentStep,
      });
      
      // Submit the application
      const response = await axios.post(`${API}/applications/${applicationId}/submit`);
      if (response.data.success) {
        // HF-Draft-Leak-1 — clear the per-user draft (and its step marker).
        // The legacy unscoped key is purged on mount; removing it here too is
        // belt-and-braces for a tab that was open across the deploy.
        try {
          localStorage.removeItem("solara_draft");
          if (draftKey) {
            localStorage.removeItem(draftKey);
            localStorage.removeItem(`solara_draft_step:${user.id}`);
          }
        } catch { /* blocked */ }
        const created = response.data.doc_requests_created || 0;
        // PR #27: surface the auto-created checklist in the success toast and
        // redirect the borrower straight to their checklist page in the
        // client portal so they can start uploading right away.
        const message = response.data.completed_after_reopen
          ? response.data.message
          : created > 0
          ? `Submitted! ${response.data.application_reference} — ${created} document checklist items ready for you.`
          : `Application submitted successfully! Reference: ${response.data.application_reference}`;
        toast.success(message);
        setCompletedSteps([...STEPS.map((s) => s.id)]);

        setTimeout(() => {
          if (user) {
            navigate(`/portal/applications/${applicationId}`);
          } else {
            navigate("/");
          }
        }, 1500);
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      // PR HF-Apply-4 — surface the real backend error message so the user
      // (and we, debugging) can see WHY submission failed. The generic
      // "Failed to submit application" toast hid useful detail like
      // "Application has no loan_type set" or validation errors.
      const detail = error?.response?.data?.detail
        || error?.message
        || "Failed to submit application. Please try again.";
      toast.error(`Submission failed: ${detail}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    const stepProps = {
      formData,
      updateFormData,
      applicationId,
      API,
      prefillData,
    };

    switch (currentStep) {
      case 1:
        return (
          <>
            <StepClientRegistration
              registrationData={registrationData}
              setRegistrationData={setRegistrationData}
              user={user}
              applicationId={applicationId}
              applicationRef={applicationRef}
            />
          </>
        );
      case 2:
        return <StepLoanType {...stepProps} />;
      case 3:
        return <StepCompanyBorrower {...stepProps} />;
      case 4:
        return <StepGuarantors {...stepProps} />;
      case 5:
        return <StepAssetsLiabilities {...stepProps} />;
      case 6:
        return <StepSecurityDetails {...stepProps} />;
      case 7:
        return <StepLoanDetails {...stepProps} />;
      case 8:
        return <StepDeclarations {...stepProps} />;
      case 9:
        return <StepDocumentsReview {...stepProps} onSubmit={handleSubmit} isLoading={isLoading} />;
      default:
        return null;
    }
  };

  // HF-Session-Leak-2 (2026-09-16) — identity-confirmation gate. `user` can
  // be non-null purely because AuthContext found a still-valid token in
  // localStorage on boot; hasFreshAuth is only true once this identity was
  // established by an explicit action in this tab this session (see
  // AuthContext.jsx). Until then, show a confirmation screen instead of the
  // wizard — the effect that fetches/hydrates a previous draft is itself
  // gated on hasFreshAuth, so no application PII has been requested or
  // rendered at this point; this is purely a "continue as / not you" choice.
  //
  // This runs after every hook in the component has already been declared
  // above (rules-of-hooks safe) — it only affects what gets rendered.
  if (user && !hasFreshAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#021d49] p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center" data-testid="identity-confirm-gate">
          <img src={LOGO_HORIZONTAL} alt="Solara Global" className="h-8 mx-auto mb-6" />
          <h1 className="text-xl font-semibold text-[#021d49] mb-2">Welcome back</h1>
          <p className="text-gray-600 mb-1">This browser is already signed in as</p>
          <p className="font-medium text-[#021d49] mb-6 break-words" data-testid="identity-confirm-name">
            {maskIdentityForConfirm(user)}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Continuing will resume this account's in-progress application. If this
            isn't you — for example, someone else used this device before you —
            sign in with your own details instead.
          </p>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={confirmIdentity}
              className="w-full bg-[#021d49] text-white py-3 rounded-md font-medium hover:bg-[#052b66] transition-colors"
              data-testid="identity-confirm-continue"
            >
              Yes, continue as {user.name || user.email}
            </button>
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate(`/login?return=${encodeURIComponent("/apply")}`);
              }}
              className="w-full border border-gray-300 text-gray-700 py-3 rounded-md font-medium hover:bg-gray-50 transition-colors"
              data-testid="identity-confirm-not-you"
            >
              Not you? Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="loan-app flex flex-col md:flex-row min-h-screen">
      {/* PR #95 / UX-1 — mount the shared confirm dialog */}
      {confirmDialog}
      {/* Mobile Header */}
      <div className="md:hidden bg-[#021d49] text-white p-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => handleProtectedNavigation(() => navigate("/"))} className="flex items-center gap-3" data-testid="mobile-logo-link">
            <img 
              src={LOGO_HORIZONTAL} 
              alt="Solara Global" 
              className="h-10 brightness-0 invert"
              data-testid="mobile-logo"
            />
          </button>
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mb-4 p-4 bg-[#052b66] rounded">
            <div className="flex flex-col gap-2">
              <button
                onClick={() => downloadPdf(`${API}/pdf/blank`, "Solara_Loan_Application_Blank.pdf")}
                className="flex items-center gap-2 text-sm text-gray-300 hover:text-white py-2"
                data-testid="mobile-download-blank"
              >
                <Download className="w-4 h-4" />
                Download Printable Form
              </button>
              {applicationId && (
                <button
                  onClick={() => downloadPdf(`${API}/applications/${applicationId}/pdf`, `Solara_Application_${applicationRef || 'draft'}.pdf`)}
                  className="flex items-center gap-2 text-sm text-[#bd9a59] hover:text-white py-2"
                  data-testid="mobile-download-my-pdf"
                >
                  <Download className="w-4 h-4" />
                  Download My Application
                </button>
              )}
              <button onClick={() => handleProtectedNavigation(() => navigate("/"))} className="text-sm text-left text-gray-300 hover:text-white py-2">
                ← Back to Home
              </button>
            </div>
          </div>
        )}
        
        {/* Mobile Step Indicator */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
          {STEPS.map((step) => (
            <button
              key={step.id}
              onClick={() => guardedStepClick(step.id)}
              data-testid={`mobile-step-${step.id}`}
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                completedSteps.includes(step.id)
                  ? "bg-green-500 text-white"
                  : currentStep === step.id
                  ? "bg-[#bd9a59] text-white"
                  : "bg-[#052b66] text-gray-300"
              }`}
            >
              {completedSteps.includes(step.id) ? (
                <Check className="w-4 h-4" />
              ) : (
                step.id
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col w-80 lg:w-96 bg-[#021d49] text-white p-8 sticky top-0 h-screen overflow-y-auto custom-scrollbar">
        {/* Logo & Branding */}
        <div className="mb-8">
          <button onClick={() => handleProtectedNavigation(() => navigate("/"))} className="block mb-4 text-left" data-testid="sidebar-logo-link">
            <img 
              src={LOGO_HORIZONTAL} 
              alt="Solara Global Capital Partners" 
              className="h-12 brightness-0 invert"
              data-testid="sidebar-logo"
            />
          </button>
          <p className="text-xs text-gray-400 tracking-wider">TRANSPARENCY • SECURITY • INTEGRITY</p>
          <a 
            href="https://www.solaraglobal.com.au" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#bd9a59] text-sm hover:underline mt-2 inline-block"
            data-testid="website-link"
          >
            www.solaraglobal.com.au
          </a>
        </div>

        {/* Application Reference — HF-Apply-8 (2026-06-24): show a
            skeleton placeholder while the draft loads so the sidebar
            doesn't look empty on a returning client. Prevents the
            "wait, did I lose my draft?" moment. */}
        {applicationRef ? (
          <div className="mb-6 p-3 bg-[#052b66] rounded" data-testid="application-reference">
            <p className="text-xs text-gray-400 mb-1">Application Reference</p>
            <p className="font-mono text-[#bd9a59] text-sm">{applicationRef}</p>
          </div>
        ) : user ? (
          draftLoadTimedOut ? (
            /* APPLY-3 (2026-07-08) — the skeleton previously pulsed forever if
               the draft request hung (LOW: no timeout/error state). */
            <div className="mb-6 p-3 bg-red-900/30 border border-red-400/30 rounded" data-testid="application-reference-timeout">
              <p className="text-xs text-red-200 mb-1">We couldn't load your draft.</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="text-sm text-[#bd9a59] underline"
              >
                Reload the page to try again
              </button>
            </div>
          ) : (
            <div className="mb-6 p-3 bg-[#052b66]/40 rounded animate-pulse" data-testid="application-reference-loading">
              <p className="text-xs text-gray-400/70 mb-1">Application Reference</p>
              <p className="font-mono text-[#bd9a59]/40 text-sm">Loading…</p>
            </div>
          )
        ) : null}

        {/* Step Navigation */}
        <nav className="space-y-1">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-4">Application Progress</p>
          {STEPS.map((step) => {
            const Icon = step.icon;
            const isCompleted = completedSteps.includes(step.id);
            const isActive = currentStep === step.id;
            const isAccessible = step.id <= currentStep || completedSteps.includes(step.id - 1);

            return (
              <button
                key={step.id}
                onClick={() => guardedStepClick(step.id)}
                disabled={!isAccessible}
                title={!isAccessible ? "Complete the earlier steps first to unlock this one" : undefined}
                data-testid={`sidebar-step-${step.id}`}
                className={`w-full text-left p-3 rounded transition-all flex items-center gap-3 ${
                  isActive
                    ? "bg-[#bd9a59]/20 border-l-4 border-[#bd9a59]"
                    : isCompleted
                    ? "bg-green-500/10 border-l-4 border-green-500"
                    : "border-l-4 border-transparent hover:bg-[#052b66]"
                } ${!isAccessible ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isCompleted
                      ? "bg-green-500"
                      : isActive
                      ? "bg-[#bd9a59]"
                      : "bg-[#052b66]"
                  }`}
                >
                  {isCompleted ? (
                    <Check className="w-4 h-4 text-white animate-check" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-medium ${
                      isActive ? "text-[#bd9a59]" : isCompleted ? "text-green-400" : "text-gray-300"
                    }`}
                  >
                    {step.title}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{step.description}</p>
                </div>
              </button>
            );
          })}
        </nav>

        {/* Help Section */}
        <div className="mt-auto pt-6">
          <div className="p-4 bg-[#052b66] rounded mb-4">
            <p className="text-sm font-medium mb-2">Download Forms</p>
            <button
              onClick={() => downloadPdf(`${API}/pdf/blank`, "Solara_Loan_Application_Blank.pdf")}
              className="flex items-center gap-2 text-sm text-gray-300 hover:text-white mb-2 w-full text-left"
              data-testid="download-blank-btn"
            >
              <Download className="w-4 h-4" />
              Printable Blank Form
            </button>
            {applicationId && (
              <button
                onClick={() => downloadPdf(`${API}/applications/${applicationId}/pdf`, `Solara_Application_${applicationRef || 'draft'}.pdf`)}
                className="flex items-center gap-2 text-sm text-[#bd9a59] hover:text-white w-full text-left"
                data-testid="download-my-pdf-btn"
              >
                <Download className="w-4 h-4" />
                My Application PDF
              </button>
            )}
          </div>
          <div className="p-4 bg-[#052b66] rounded">
            <p className="text-sm font-medium mb-2">Need Assistance?</p>
            <p className="text-xs text-gray-400 mb-3">Our team is here to help with your application.</p>
            <a 
              href="mailto:loans@solaraglobal.com.au" 
              className="text-[#bd9a59] text-sm hover:underline"
              data-testid="contact-email"
            >
              loans@solaraglobal.com.au
            </a>
          </div>
        </div>
      </aside>

      {/* Main Form Content */}
      <main className="flex-1 bg-[#fafaf9] min-h-screen">
        {/* Progress Bar */}
        <div className="progress-bar sticky top-0 z-10">
          <div
            className="progress-fill"
            style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
          />
        </div>

        <div className="p-6 md:p-10 lg:p-16 max-w-5xl mx-auto">
          {/* Step Header */}
          <div className="mb-8 animate-fade-in">
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-sm text-[#bd9a59] font-medium">
                Step {currentStep} of {STEPS.length}
              </p>
              {/* APP-FORM-FIX (2026-07-25) — subtle background auto-save
                  indicator. Deliberately not a toast: it fires on every
                  debounced edit and a toast per keystroke pause would be
                  noisy. */}
              {autoSaveStatus !== "idle" && (
                <span
                  className="flex items-center gap-1.5 text-xs text-gray-400 select-none"
                  data-testid="autosave-indicator"
                >
                  {autoSaveStatus === "saving" ? (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full bg-gray-400 animate-pulse" aria-hidden="true" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5 text-green-500" aria-hidden="true" />
                      Saved
                    </>
                  )}
                </span>
              )}
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#021d49] mb-2">
              {STEPS[currentStep - 1].title}
            </h2>
            <p className="text-[#64748b]">{STEPS[currentStep - 1].description}</p>
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg animate-fade-in scroll-mt-28" data-testid="validation-errors">
              <p className="text-red-700 font-semibold text-sm mb-2">Please complete the following:</p>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((err, i) => (
                  <li key={i} className="text-red-600 text-sm">{err}</li>
                ))}
              </ul>
            </div>
          )}

          {enquiryRef && !enquiryBannerHidden && (
            <div className="mb-6 p-4 md:p-5 rounded-md border border-[#bd9a59]/50 bg-[#bd9a59]/10 text-[#021d49] flex gap-3 items-start" data-testid="enquiry-received-banner" role="status">
              <Check className="w-5 h-5 mt-0.5 text-[#bd9a59] flex-shrink-0" />
              <div className="flex-1 text-sm leading-relaxed">
                <p className="font-semibold text-base mb-1">Your enquiry has been received</p>
                <p>
                  Reference <span className="font-semibold">{enquiryRef}</span>. A Solara specialist will be in touch.
                  To move things along, complete the remaining steps of your loan application below.
                </p>
                {currentStep === 1 && !user && leadPrefill && (
                  <p className="mt-2" data-testid="lead-prefill-note">
                    We've filled in your details from the enquiry. Check them, choose a password and continue.
                  </p>
                )}
              </div>
              <button type="button" onClick={closeEnquiryBanner} className="text-[#021d49]/60 hover:text-[#021d49]" aria-label="Dismiss" data-testid="enquiry-banner-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step Content */}
          <div className="animate-fade-in" data-testid={`step-${currentStep}-content`}>
            {renderStepContent()}
          </div>

          {/* Navigation Buttons */}
          {/* PR HF-Apply-1 (2026-06-03) — bumped from 8 to 9 after Documents & Review
              was added as step 9. Declarations (step 8) is NOT the final step any
              more, so the standard Previous/Continue footer must still render here. */}
          {currentStep < 9 && (
            <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200">
              <button
                onClick={guardedPrevious}
                disabled={currentStep === 1}
                data-testid="btn-previous"
                className={`flex items-center gap-2 px-6 py-3 rounded font-medium transition-all ${
                  currentStep === 1
                    ? "opacity-50 cursor-not-allowed text-gray-400"
                    : "btn-secondary hover:bg-gray-100"
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <div className="flex gap-3">
                {currentStep > 1 && (
                  <button
                    onClick={async () => {
                      setIsLoading(true);
                      const saved = await saveProgress();
                      setIsLoading(false);
                      if (saved) toast.success("Progress saved");
                    }}
                    disabled={isLoading}
                    data-testid="btn-save"
                    className="btn-secondary px-6 py-3 rounded"
                  >
                    {isLoading ? "Saving..." : "Save Progress"}
                  </button>
                )}
                <button
                  onClick={handleNext}
                  // HF-Apply-8 (2026-06-24) — also disable when the user
                  // is signed in but the draft hasn't hydrated yet.
                  // Previously the button was clickable in this window
                  // and saveProgress would no-op (or, post-HF-Apply-7,
                  // show "Your draft isn't loaded yet"). Either way the
                  // user thinks the form is broken. Disabling the
                  // button is the only honest answer.
                  disabled={isLoading || (user && currentStep === 1 && !applicationId)}
                  data-testid="btn-next"
                  className="btn-accent flex items-center gap-2 px-6 py-3 rounded disabled:opacity-60 disabled:cursor-wait"
                >
                  {/* PR HF-Apply-2 — explicit loading text so the user sees
                      something happened. Combined with inFlightRef guard, this
                      prevents the "I clicked Continue and nothing happened so
                      I clicked again and now I have 4 applications" failure. */}
                  {isLoading ? (
                    <>
                      <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-r-transparent animate-spin" aria-hidden="true" />
                      {currentStep === 1 && !user
                        ? (registrationData.signin_mode ? "Signing in…" : "Creating account…")
                        : "Saving…"}
                    </>
                  ) : (
                    <>
                      {currentStep === 1 && !user
                        ? (registrationData.signin_mode ? "Sign In & Continue" : "Create Account & Continue")
                        : "Continue"}
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 9 Back Button (final review — Documents & Review owns its own Submit) */}
          {/* PR HF-Apply-1 — bumped from 8 to 9. Documents & Review is the actual
              final step, and StepDocumentsReview renders its own Submit button. */}
          {currentStep === 9 && (
            <div className="mt-10 pt-6 border-t border-gray-200">
              <button
                onClick={guardedPrevious}
                data-testid="btn-previous-step9"
                className="flex items-center gap-2 px-6 py-3 rounded font-medium btn-secondary hover:bg-gray-100 transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
